<script setup lang="ts">
import { OrganismReviewsProps } from "~/components/ProductPage/Reviews/OrganismReviews.props"
import { ProductDetailsProps } from "./ProductDetails.props"
import {
  mapBrands,
  mapProductsAlgolia,
  ProductAlgolia
} from "~/utils/mapProducts"
import {
  VIEW_SAME_CATEGORY_PDP,
  VIEW_ACTIVE_PRINCIPLE_PDP,
  FREQUENTLY_BOUGHT_TOGETHER
} from "~~/config/algolia-insights"
import { insightsAlgoliaMiddleware } from "~/utils/insightsAlgoliaMiddleware"
import { ContainerProps } from "~/components/Banner/ProductPage/Support/Container.props"
import type {
  WithContext,
  Drug,
  Product,
  FAQPage,
  ProductGroup
} from "schema-dts"
import { FastShippingProps } from "~/components/Banner/Delivery/FastShipping.props"
import { DefaultProps } from "~/components/ProductPage/Banner/Default.props"
import {
  AisInstantSearch,
  AisConfigure,
  AisHits,
  AisIndex
  // @ts-ignore
} from "vue-instantsearch/vue3/es/index.js"
import { pricePerUnit } from "~/utils/formatters"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { productUtils } from "~/utils/product"
import { excludeOutOfMarketRuleContext } from "~/utils/algolia"
import { cleanJsonLdObject } from "~/utils/jsonLd"
import { availabilityLeadEvent } from "~/utils/analytics/availabilityLead"
import { ResultsProps } from "~/components/ProductTile/Overall/Results.props"
import { GA4Entities } from "nuxt-ga4"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = defineProps<ProductDetailsProps>()
const emit =
  defineEmits<{
    (e: "onPurchase", value: number, isSticky: boolean): void
    (e: "onSaveForLater"): void
  }>()

const isLoading = ref(true)

// As per TF-2292, making the "description" a ref so whenever the "correspondingGenericDrug" values are available, re-render it
const newDescription = ref(props.description)

const reviewsProduct = ref<HTMLElement | null>(null)
const alternativeProducts = ref<HTMLElement | null>(null)
const positionTopReview = computed(() =>
  reviewsProduct.value ? useElementBounding(reviewsProduct).top : undefined
)

const isLogged = useIsLogged()

const skuState = useState("product-unavailable-state")
const { inStockSubscriptionItem, sendSubscription } = useInStockSubscription()

const insightsMiddlewareSameCategory = insightsAlgoliaMiddleware(
  VIEW_SAME_CATEGORY_PDP
)
const insightsMiddlewareActivePrinciple = insightsAlgoliaMiddleware(
  VIEW_ACTIVE_PRINCIPLE_PDP
)
const insightsMiddlewareFrequentlyBoughtTogether = insightsAlgoliaMiddleware(
  FREQUENTLY_BOUGHT_TOGETHER
)
const { t } = useI18n()
const { $cl } = useNuxtApp()

const { feedatyRatingLabel } = useFeedatyRating()

const quantity = ref(1)

const handleChangeQuantity = (newQuantity: number) => {
  quantity.value = newQuantity
}

const isAvailableAtLocalWareHouse = computed(
  () => quantity.value < (props.details.localWarehouseStock ?? 0)
)

const supportCardsData = computed<ContainerProps>(() => {
  return {
    infoCard1: {
      iconType: "call",
      title: t("supportBanner.cards.helpdesk.title"),
      text: t("supportBanner.cards.helpdesk.text"),
      link: t("supportBanner.cards.helpdesk.link", {
        link: customPaths.contactUs
      }),
      linkText: t("supportBanner.cards.helpdesk.linkText"),
      sku: props.details?.productId
    },
    infoCard2: {
      iconType: "mail",
      title: t("supportBanner.cards.callPharma.title"),
      text: t("supportBanner.cards.callPharma.text"),
      link: t("supportBanner.cards.callPharma.link", {
        link: customPaths.contactUs
      }),
      linkQuery: t("supportBanner.cards.callPharma.linkQuery", {
        param: helpSubjectParams.consulting_pharma
      }),
      linkText: t("supportBanner.cards.callPharma.linkText"),
      sku: props.details?.productId
    }
  }
})

const bannerDefaultData = computed<DefaultProps>(() => {
  return {
    banners: [
      {
        title: t("productPage.banners.default.safePayments"),
        iconType: "award"
      },
      {
        title: t("productPage.banners.default.freeShipping"),
        iconType: "car"
      },
      {
        title: t("productPage.banners.default.orderTracking"),
        iconType: "box"
      },
      {
        title: t("productPage.banners.default.subscriberOffers"),
        iconType: "pricetag"
      },
      {
        title: t("productPage.banners.default.quickReturns"),
        iconType: "return"
      },
      {
        title: t("productPage.banners.default.feedatyReviews", {
          rating: feedatyRatingLabel
        }),
        iconType: "star"
      }
    ],
    bgImageLarge: getImageData({
      altText: "Placeholder",
      url: "/i5oh5hzamkwo/m8mOjBmE8iHbnJM1y6CYY/d8e244e5a3c52e562765764c5c8ff73a/background2.svg",
      width: 1222,
      height: 154
    })
  }
})

const productCode = props.details.parentProductId ?? props.details.productId

const starsRatings = ref(0)
const valueRatings = ref(0)
const searchBarText = ref("")
const currentReviewsPage = ref(1)
const selectedStarRating = ref<number>()

const REVIEWS_FIRST_PAGE_SIZE = 3
const REVIEWS_PAGE_SIZE = 10

const mainCategorySlug = computed(() => {
  const lengthLinks = props.breadcrumbs?.links?.length
  const lastLink = props.breadcrumbs?.links?.[lengthLinks - 1]

  if (lastLink && typeof lastLink.to === "string") {
    return lastLink.to.slice(3)
  } else {
    return ""
  }
})

const { data: _relatedProductsData } = useLazyFetch<any>(
  `/api/algolia/getRecommendations/recommend-11/${
    props.details.parentProductId ?? props.details.productId
  }/${mainCategorySlug.value}`,
  { server: false }
)

const relatedProductsData = computed(() =>
  _relatedProductsData?.value
    ? mapProductsAlgolia(_relatedProductsData?.value?.results?.[0]?.hits)
    : []
)

const { data: reviewsSummary, pending: pendingTotalReviews } = useLazyFetch(
  `/api/reviews/${productCode}/summary`,
  { server: false }
)

watch(
  reviewsSummary,
  () => {
    if (reviewsSummary.value) {
      valueRatings.value = reviewsSummary.value.reviewsCount ?? 0
      starsRatings.value = reviewsSummary.value.reviewsAverage ?? 0
    }
  },
  { immediate: true }
)

const { data: reviewsList, pending: loadingReviews } = useLazyFetch(
  `/api/reviews/${productCode}`,
  {
    server: false,
    query: computed(() => {
      const page = currentReviewsPage.value
      const pageSize = page <= 1 ? REVIEWS_FIRST_PAGE_SIZE : REVIEWS_PAGE_SIZE
      return {
        skip:
          page <= 1
            ? 0
            : REVIEWS_FIRST_PAGE_SIZE + (page - 2) * REVIEWS_PAGE_SIZE,
        pageSize,
        ...(selectedStarRating.value
          ? { rating: selectedStarRating.value }
          : {})
      }
    })
  }
)

const reviews = computed((): OrganismReviewsProps => {
  const search = searchBarText.value.toUpperCase()
  const list = reviewsList.value?.reviews ?? []
  const filterReviews =
    search !== ""
      ? list.filter((singleReview: any) => {
          const textReview = (singleReview.textReview ?? "").toUpperCase()
          const merchantReview = (
            singleReview.merchantReview ?? ""
          ).toUpperCase()
          return textReview.includes(search) || merchantReview.includes(search)
        })
      : list

  const total = reviewsList.value?.totalReviews ?? 0
  const totalPages =
    total <= REVIEWS_FIRST_PAGE_SIZE
      ? total > 0
        ? 1
        : 0
      : 1 + Math.ceil((total - REVIEWS_FIRST_PAGE_SIZE) / REVIEWS_PAGE_SIZE)

  return {
    productName: props.details.title,
    isProductDrug: (newDescription?.value?.descriptions?.length ?? 0) > 1, // Drugs have more than one description
    reviewNumber: valueRatings.value,
    reviewAverage: starsRatings?.value?.toString() ?? "0",
    statsReviewAverage: (
      reviewsSummary.value?.statsReviewAverage ?? starsRatings.value
    )?.toString(),
    reviewsByStars: reviewsSummary.value?.reviewsByStars ?? [],
    searchSettings: {
      name: "search-reviews",
      label: "",
      placeholder: t("productPage.organismReviews.findKeyword"),
      modelValue: searchBarText.value,
      doNotOpenPopup: true
    },
    productReviews: filterReviews,
    currentPage: currentReviewsPage.value,
    totalPages,
    selectedStarFilter: selectedStarRating.value ?? null,
    pendingReviews: loadingReviews.value
  }
})

const scrollToReviews = () => {
  const header = document.querySelector("header")
  const headerHeight = header ? header.offsetHeight + 50 : 0
  if (typeof positionTopReview.value === "number") {
    window.scrollTo({
      top: positionTopReview.value - headerHeight,
      behavior: "smooth"
    })
  }
}

const scrollToAlternativeProducts = (behavior: ScrollBehavior = "smooth") => {
  const target =
    alternativeProducts.value || document.getElementById("alternativeProducts")
  if (!target) return false

  const header = document.querySelector("header")
  const headerHeight = header ? header.offsetHeight + 50 : 0
  const top = target.getBoundingClientRect().top + window.scrollY - headerHeight

  window.scrollTo({
    top: Math.max(top, 0),
    behavior
  })

  return true
}

const hasPendingAlternativeProductsScroll = ref(false)

const canScrollToAlternativeProducts = computed(
  () =>
    hasPendingAlternativeProductsScroll.value &&
    !!alternativeProducts.value &&
    !mccSkuListLoading.value &&
    !pendingBoughtTogetherProducts.value
)

const breadcrumbs = computed(() => {
  return {
    ...props.breadcrumbs,
    hideFirstItemInMobile: true,
    links: [{ label: "Home", to: "/" }, ...props.breadcrumbs?.links]
  }
})

const searchClient = useAlgoliaRef()

const checkLastViewedProductsWithCookies = () => {
  const lastViewedProducts = useStatefulCookie("lastProductsViewed", {
    default: () => []
  })

  if (lastViewedProducts.value) {
    const productId = props.details.productId
    lastViewedProducts.value = (productId ? [productId] : []).concat(
      lastViewedProducts.value.filter((i) => i !== productId).slice(0, 10)
    )
  } else {
    lastViewedProducts.value = [props.details.productId]
  }
}

const {
  data: boughtTogetherProductsResults,
  pending: pendingBoughtTogetherProducts,
  error: errorBoughtTogetherProducts
} = useLazyFetch<any>(
  `/api/algolia/getRecommendations/recommend-2/${
    props.details.parentProductId ?? props.details.productId
  }`,
  {
    server: false
  }
)

onMounted(async () => {
  const scrollFromStorage = sessionStorage.getItem("scrollToAlternatives")
  if (props.scrollToAlternativeProducts || scrollFromStorage) {
    sessionStorage.removeItem("scrollToAlternatives")
    hasPendingAlternativeProductsScroll.value = true
  }

  try {
    skuState.value = props.details.productId

    checkLastViewedProductsWithCookies()
    fetchMccSkuList(props.details.productId)
  } catch (err) {
    console.log(err)
  } finally {
    isLoading.value = false
  }
})

watch(
  canScrollToAlternativeProducts,
  async (shouldScroll) => {
    if (!shouldScroll) return

    await nextTick()
    requestAnimationFrame(() => {
      const didScroll = scrollToAlternativeProducts("smooth")

      if (didScroll) {
        hasPendingAlternativeProductsScroll.value = false
      }
    })
  },
  { immediate: true, flush: "post" }
)

const boughtTogetherProducts = useAlgoliaMappedProducts(
  boughtTogetherProductsResults
)

/** get event event_select_item_{productCode} */
const { event: productAvailableWarning } = useGAEvent(
  `track_product_availability_warning_${props.details?.productId ?? ""}_`,
  "custom_event"
)
/** Define single GA4 item */
const GAItem = computed((): GA4Entities["gaItem"] => {
  if (Object.keys(productAvailableWarning.value).length) {
    return productAvailableWarning.value?.items?.[0] ?? {}
  } else {
    return {
      index: 0,
      item_id: props.details?.productId ?? "",
      item_name: props.details?.title ?? "",
      item_list_name: "",
      item_brand: props.brand?.brandName ?? "",
      price: parseFloat(props.details?.price?.toString()) ?? "",
      discount:
        props.details?.oldPrice && props.details?.price
          ? props.details.oldPrice - props.details.price
          : "",
      item_category: props.breadcrumbs?.links[0]?.label ?? "",
      item_category2: props.breadcrumbs?.links[1]?.label ?? "",
      item_category3: props.breadcrumbs?.links[2]?.label ?? "",
      item_category4: props.breadcrumbs?.links[3]?.label ?? "",
      item_category5: props.breadcrumbs?.links[4]?.label ?? "",
      item_category6: props.breadcrumbs?.links[5]?.label ?? "",
      available: props.details.stockQuantity ? "si" : "no",
      quantity: props.details.stockQuantity ?? 0,
      bundle_name: "",
      currency: "EUR"
    }
  }
})

/**
 * @description Send event when the user sends a notification request
 */
const sendAvailabilityLeadEvent = async () => {
  const cat =
    props.breadcrumbs?.links[props.breadcrumbs?.links.length - 1].label

  inStockSubscriptionItem.value = {
    event: availabilityLeadEvent.availabilityLead,
    category: cat,
    action: props.brand?.brandName ?? "",
    label: props.details?.title ?? ""
  }

  await sendSubscription()
}

if (import.meta.server) {
  const baseUrl = useRuntimeConfig().public.BASE_URL_APP
  const jsonLd: WithContext<Drug | Product | ProductGroup> =
    // Drugs have more than one description
    (newDescription?.value?.descriptions?.length ?? 0) > 1
      ? {
          "@context": "https://schema.org",
          "@type":
            props.multipackOptions && props.multipackOptions.length > 1
              ? "ProductGroup"
              : "Drug",
          "@id": `#product_${props.details.productId}`,
          name: props.details.title,
          description: props.details.description,
          image: props.details.gallery
            .map((el) => el?.image?.url)
            .filter(Boolean),
          mpn: props.details.productId,
          sku: props.details.productId,
          url: `${baseUrl}${useRoute().fullPath}`,
          activeIngredient: props.activeSubstance,
          administrationRoute: newDescription.value.infos.howToUse,
          ...(props.activeSubstance
            ? {
                availableStrength: {
                  "@type": "DrugStrength",
                  activeIngredient: props.activeSubstance
                }
              }
            : {}),
          dosageForm: props.details.unitType,
          ...(props.activeSubstance
            ? {
                drugClass: {
                  "@type": "DrugClass",
                  name: props.activeSubstance
                }
              }
            : {}),
          drugUnit: newDescription.value.infos.dosageType,
          legalStatus: {
            "@type": "DrugPrescriptionStatus",
            name: "OTC"
          },
          category: newDescription.value.infos.category,
          offers: {
            "@type": "Offer",
            price: props.details.price,
            priceCurrency: "EUR",
            url: `${baseUrl}${useRoute().fullPath}`,
            itemCondition: "http://schema.org/NewCondition",
            availability:
              props.details.stockQuantity === 0
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: props.details.price > 19.9 ? 0 : 4.9,
                currency: "EUR"
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "IT",
                postalCodeRange: {
                  postalCodeBegin: "00010",
                  postalCodeEnd: "97100"
                }
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: {
                  "@type": "QuantitativeValue",
                  minValue: "0",
                  maxValue: "1"
                },
                transitTime: {
                  "@type": "QuantitativeValue",
                  minValue: "1",
                  maxValue: "4"
                },
                cutOffTime: "18:30-08:00",
                businessDays: {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: [
                    "https://schema.org/Monday",
                    "https://schema.org/Tuesday",
                    "https://schema.org/Wednesday",
                    "https://schema.org/Thursday",
                    "https://schema.org/Friday"
                  ]
                }
              }
            },
            seller: {
              "@type": "Organization",
              url: "https://www.topfarmacia.it/",
              name: "Top Farmacia",
              "@id": "#store-organization",
              logo: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
            }
          },
          ...(!!props.brand
            ? {
                brand: {
                  "@type": "Brand",
                  name: props.brand?.brandName,
                  logo: props.brand?.brandBanner?.brandImage?.image?.url
                }
              }
            : {}),
          ...(props.reviews?.aggregateRating?.reviewCount &&
          props.reviews.aggregateRating?.reviewCount !== "0"
            ? {
                aggregateRating: props.reviews?.aggregateRating
              }
            : {}),
          ...((props.reviews?.review?.length ?? 0) > 0
            ? {
                review: props.reviews?.review
              }
            : {})
        }
      : {
          "@context": "https://schema.org",
          "@type":
            props.multipackOptions && props.multipackOptions.length > 1
              ? "ProductGroup"
              : "Product",
          "@id": `#product_${props.details.productId}`,
          name: props.details.title,
          description: props.details.description,
          image: props.details.gallery.map((el) => el?.image?.url),
          mpn: props.details.productId,
          sku: props.details.productId,
          url: `${baseUrl}${useRoute().fullPath}`,
          category: newDescription.value.infos.category,
          offers: {
            "@type": "Offer",
            price: props.details.price,
            priceCurrency: "EUR",
            url: `${baseUrl}${useRoute().fullPath}`,
            priceValidUntil: "2024-04-22",
            itemCondition: "http://schema.org/NewCondition",
            availability:
              props.details.stockQuantity === 0
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: props.details.price > 19.9 ? 0 : 4.9,
                currency: "EUR"
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "IT",
                postalCodeRange: {
                  postalCodeBegin: "00010",
                  postalCodeEnd: "97100"
                }
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: {
                  "@type": "QuantitativeValue",
                  minValue: "0",
                  maxValue: "1"
                },
                transitTime: {
                  "@type": "QuantitativeValue",
                  minValue: "1",
                  maxValue: "4"
                },
                cutOffTime: "18:30-08:00",
                businessDays: {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: [
                    "https://schema.org/Monday",
                    "https://schema.org/Tuesday",
                    "https://schema.org/Wednesday",
                    "https://schema.org/Thursday",
                    "https://schema.org/Friday"
                  ]
                }
              }
            },
            seller: {
              "@type": "Organization",
              url: "https://www.topfarmacia.it/",
              name: "Top Farmacia",
              "@id": "#store-organization",
              logo: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
            }
          },
          ...(!!props.brand
            ? {
                brand: {
                  "@type": "Brand",
                  name: props.brand?.brandName,
                  logo: props.brand?.brandBanner?.brandImage?.image?.url
                }
              }
            : {}),
          ...(props.reviews?.aggregateRating?.reviewCount &&
          props.reviews.aggregateRating?.reviewCount !== "0"
            ? {
                aggregateRating: props.reviews?.aggregateRating
              }
            : {}),
          ...((props.reviews?.review?.length ?? 0) > 0
            ? {
                review: props.reviews?.review
              }
            : {})
        }

  if (props.multipackOptions && props.multipackOptions.length > 1) {
    ;(jsonLd as WithContext<ProductGroup>).hasVariant = props.multipackOptions
      .filter((option) => !option.current)
      .map((option) => ({
        "@type": "Product",
        "@id": `#product_${option.productCode}`,
        name: option.productName,
        sku: option.productCode,
        price: option.price,
        priceCurrency: "EUR",
        url: `${baseUrl}${option.slug}`
      }))
  }

  let structuredData: WithContext<any>[] = [jsonLd]

  if (props.faq?.questions?.length > 0) {
    const faqJsonLd: WithContext<FAQPage> = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${baseUrl}${useRoute().fullPath}#faq`,
      url: `${baseUrl}${useRoute().fullPath}`,
      name: props.details.title,
      mainEntity: props.faq.questions.map((question, index) => ({
        "@type": "Question",
        name: question.question,
        "@id": `${baseUrl}${useRoute().fullPath}#faq_${index}`,
        "@context": "https://schema.org/",
        acceptedAnswer: {
          "@type": "Answer",
          "@id": `${baseUrl}${useRoute().fullPath}#faq_${index}_answer`,
          text: question.answer,
          "@context": "https://schema.org/"
        }
      }))
    }
    structuredData.push(faqJsonLd)
  }

  useHead({
    script: structuredData.map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(data))
    }))
  })
}

const genericDrugFilter = `[["correspondingEquivalentDrugs:${props.details.productId}"],["genericDrug:true"]]`

const sameActiveSubstanceFilter = computed(() =>
  props.activeSubstance
    ? [`activeSubstance:${props.activeSubstance}`, "inStock:true"]
    : undefined
)

const sameCategoryFilter = computed(() =>
  mainCategorySlug.value
    ? [`mainCategory.slug:${mainCategorySlug.value}`]
    : undefined
)

const searchFunction = (helper: any) => {
  helper.setQueryParameter("clickAnalytics", true).search()
}

const {
  mccSkuCodes,
  mccPromotion,
  loading: mccSkuListLoading,
  fetchMccSkuList
} = useMccSkuList()
</script>

<template>
  <div>
    <UtilsBreadcrumbs v-if="breadcrumbs" v-bind="breadcrumbs" />

    <div class="grid-container padded relative">
      <!-- TODO: SHAPE TO ANIMATE -->
      <UtilsIcon
        name="Shape.svg"
        class="absolute left-0 top-[500px] -z-10 hidden md:block"
      />
      <div class="grid-container col-span-full my-3 md:my-5">
        <div class="col-span-full md:col-span-8">
          <ProductPageDetail
            v-bind="details"
            :expirationDate="details.expirationDate"
            :sellabilityStatus="sellabilityStatus"
            :product-id="details.productId"
            :ratings="valueRatings"
            :stars="Math.round(starsRatings)"
            :pending-ratings="pendingTotalReviews"
            :available-at-local-ware-house="isAvailableAtLocalWareHouse"
            @on-reviews="scrollToReviews"
          />
        </div>
        <div class="col-span-full md:col-span-4">
          <ProductPagePurchaseTypeSingle
            v-if="details.stockQuantity"
            class="bg-white"
            :class="{ hidden: details.price === 0 }"
            :price="details.price"
            :price-per-unit="pricePerUnit(details.price, details.quantity)"
            :unit-type="details.unitType"
            :stock-quantity="details.stockQuantity"
            :product="{
              details,
              description,
              gaItem: GAItem
            }"
            :multipack-options="multipackOptions"
            :is-sop-otc="isSopOtc"
            :is-save-for-later-enabled="!isBundle"
            @onPurchase="
              (quantity, isSticky) => emit('onPurchase', quantity, isSticky)
            "
            @onSaveForLater="emit('onSaveForLater')"
            @onChangeQuantity="handleChangeQuantity"
          />
          <template
            v-else-if="productUtils.isSellableStatus(sellabilityStatus)"
          >
            <ClientOnly v-if="!isBundle">
              <!-- this component will only be rendered on client side -->
              <ProductPagePurchaseTypeNotAvailable
                :product="{
                  details,
                  description,
                  isBundle,
                  gaItem: GAItem
                }"
                @handle-notify-me="sendAvailabilityLeadEvent"
              />
              <template #fallback>
                <!-- this will be rendered on server side -->
                <SkeletonNotAvailable />
              </template>
            </ClientOnly>
            <template v-else>
              <!-- per https://lafarmacia.atlassian.net/browse/TF-3825, show a server-side rendered link to parent single pack option -->
              <ProductPagePurchaseTypeNotAvailableMultipack
                :product="{
                  details,
                  description,
                  isBundle,
                  singlePackOption: multipackOptions?.find(
                    (option) => option.quantity == 1
                  ),
                  gaItem: GAItem
                }"
              />
            </template>
          </template>
          <ProductPagePurchaseTypeNotSellable
            v-else
            :product-code="details?.productId"
            @handle-scroll-to-alternatives="scrollToAlternativeProducts"
          />
        </div>
      </div>

      <div class="grid-container col-span-full my-3 md:my-5">
        <!-- BUNDLE -->
        <div class="col-span-full md:col-span-8">
          <div v-if="mccSkuCodes.length">
            <ProductPageCrossSelling
              :sku-codes="mccSkuCodes"
              :percentage-discount="mccPromotion?.percentage ?? 0"
            />
          </div>
          <ProductPageBundle
            v-else-if="
              boughtTogetherProducts?.length &&
              !pendingBoughtTogetherProducts &&
              !errorBoughtTogetherProducts
            "
            :products="boughtTogetherProducts"
          />

          <ProductPageDescription
            v-bind="newDescription"
            :has-faqs="faq.questions.length !== 0"
            :is-sop-otc="isSopOtc"
          />
        </div>
        <div class="col-span-full md:col-span-4">
          <div
            v-for="promo in promos"
            :key="promo.id"
            class="col-span-full my-3 md:my-0"
          >
            <CmsComponentPromoGift
              v-bind="promo"
              v-if="promo.type === 'ComponentPromoGift'"
            />
            <CmsComponentProductPromoCode
              v-bind="promo"
              v-if="promo.type === 'ComponentProductPromoCode'"
            />
            <CmsComponentPromoCrossSelling
              v-bind="promo"
              v-if="promo.type === 'ComponentPromoCrossSelling'"
            />
          </div>
          <BannerPromoNewsletter />
          <ProductPageBannerDefault
            v-bind="bannerDefaultData"
            class="col-span-full"
          />
          <BannerProductPageSupportContainer v-bind="supportCardsData" />
        </div>
      </div>
    </div>
    <a ref="alternativeProducts" id="alternativeProducts"></a>
    <NuxtLazyHydrate>
      <AisInstantSearch
        :insights="true"
        :search-client="searchClient"
        :search-function="searchFunction"
      >
        <!-- Similar generic products -->
        <AisIndex
          :index-name="`TOP_PRODUCTS${checkEnvSite()}`"
          index-id="similar-generic-products"
        >
          <AisConfigure
            :hits-per-page.camel="15"
            :distinct="true"
            :facet-filters.camel="genericDrugFilter"
            :ruleContexts="excludeOutOfMarketRuleContext"
          />
          <AisHits
            :transform-items="(items: ResultsProps[], {results}: any) => mapProductsAlgolia(items, results.queryID)"
          >
            <template #default="{ items }">
              <ProductPageGenericProduct
                v-if="items.length"
                :title="genericDrugsTitle"
                :description="genericDrugsDescription"
                :products="items"
              />
              <div v-else></div>
            </template>
          </AisHits>
        </AisIndex>

        <!-- Same category products -->
        <AisIndex
          :index-name="`TOP_PRODUCTS${checkEnvSite()}`"
          index-id="same-category-products"
          :middlewares="[insightsMiddlewareSameCategory]"
          :v-if="mainCategorySlug"
        >
          <template v-if="relatedProductsData?.length">
            <RailsProduct
              :suggested-cards="relatedProductsData"
              class="my-3 md:my-5"
              :title="$t('productDetails.sameCategory')"
              type-card="Product"
              :skip="details.productId"
              :list-name="VIEW_ITEM_LIST_NAMES.PRODUCT"
              :list-id="VIEW_ITEM_LIST_IDS.SAME_CATEGORY"
            />
          </template>
          <template v-else>
            <AisConfigure
              :hits-per-page.camel="15"
              :distinct="true"
              :facet-filters.camel="sameCategoryFilter"
              :ruleContexts="excludeOutOfMarketRuleContext"
            />
            <AisHits
              :transform-items="(items: ResultsProps[], {results}: any) => mapProductsAlgolia(items, results.queryID)"
            >
              <template #default="{ items }">
                <RailsProduct
                  v-if="items.length"
                  :suggested-cards="items"
                  class="my-3 md:my-5"
                  :title="$t('productDetails.sameCategory')"
                  type-card="Product"
                  :skip="details.productId"
                  :list-name="VIEW_ITEM_LIST_NAMES.PRODUCT"
                  :list-id="VIEW_ITEM_LIST_IDS.SAME_CATEGORY"
                />
                <div v-else></div>
              </template>
            </AisHits>
          </template>
        </AisIndex>

        <!-- Same active principle -->
        <AisIndex
          v-if="activeSubstance"
          :index-name="`TOP_PRODUCTS${checkEnvSite()}`"
          index-id="same-active-priciple-products"
          :middlewares="[insightsMiddlewareActivePrinciple]"
        >
          <AisConfigure
            :hits-per-page.camel="15"
            :distinct="true"
            :facet-filters.camel="sameActiveSubstanceFilter"
            :ruleContexts="excludeOutOfMarketRuleContext"
          />
          <AisHits
            :transform-items="(items: ResultsProps[], {results}: any) => mapProductsAlgolia(items, results.queryID)"
          >
            <template #default="{ items }">
              <RailsProduct
                v-if="items.length"
                :suggested-cards="items"
                class="my-3 md:my-5"
                :title="$t('productDetails.sameActiveSubstance')"
                type-card="Product"
                background-color="tiffany"
                :skip="details.productId"
                :listName="VIEW_ITEM_LIST_NAMES.PRODUCT"
                :listId="VIEW_ITEM_LIST_IDS.SAME_ACTIVE_INGREDIENT"
              />
              <div v-else></div>
            </template>
          </AisHits>
        </AisIndex>

        <div class="relative pb-3 md:pb-6">
          <!-- FAQs -->

          <AisIndex
            :index-name="`TOP_PRODUCTS${checkEnvSite()}`"
            index-id="faqs"
            v-if="faq?.questions?.length"
          >
            <AisConfigure
              :hits-per-page.camel="1"
              :distinct="true"
              :facet-filters.camel="`brand.name:${brand.brandName}`"
              :ruleContexts="excludeOutOfMarketRuleContext"
            />
            <AisHits :transform-items="mapProductsAlgolia">
              <template #default="{ items }">
                <ProductPageSupport
                  v-if="faq?.questions?.length"
                  id="supporto"
                  class="padded my-10 md:my-16"
                  v-bind="faq"
                />
                <div v-else></div>
              </template>
            </AisHits>
          </AisIndex>

          <!-- Reviews -->
          <div
            id="container-reviews"
            class="grid-container padded py-3 md:py-5"
            v-if="valueRatings > 0"
          >
            <ProductPageReviewsOrganismReviews
              v-if="reviews && (reviewsList || !loadingReviews)"
              v-bind="reviews"
              ref="reviewsProduct"
              class="col-span-full mb-10 md:mb-20"
              @on-search="(newValue) => (searchBarText = newValue)"
              @on-page-change="
                (page) => {
                  currentReviewsPage = page
                  scrollToReviews()
                }
              "
              @on-star-filter="
                (rating) => {
                  selectedStarRating = rating ?? undefined
                  currentReviewsPage = 1
                }
              "
            />
            <SkeletonReviews v-else class="col-span-full mb-10 md:mb-20" />
          </div>

          <!-- Suggested brands -->
          <!-- Hide top brends [jira task] TF-2719 -->
          <AisIndex v-if="false" :index-name="`TOP_BRANDS`">
            <AisConfigure
              :hits-per-page.camel="10"
              :distinct="true"
              :facet-filters.camel="`brandName:${brand.brandName}`"
              :ruleContexts="excludeOutOfMarketRuleContext"
            />
            <AisHits :transform-items="mapBrands">
              <template #default="{ items }">
                <RailsSuggestedBrands
                  v-if="items?.length"
                  :title="$t('productDetails.suggestedBrands')"
                  :suggested-cards="items"
                  bg-color="bg-grey-main"
                />
                <div v-else></div>
              </template>
            </AisHits>
          </AisIndex>

          <BannerNewsletter class="padded" />
          <!-- TODO: shape to animate -->

          <OrganismsCartBannerDeliveryPayments
            class="md:padded"
            v-if="bannersSection"
            v-bind="bannersSection"
          />

          <div
            class="
              absolute
              left-0
              top-0
              -z-10
              hidden
              max-h-full
              overflow-hidden
              md:block
            "
          >
            <IconsShape />
          </div>
        </div>
      </AisInstantSearch>
    </NuxtLazyHydrate>
  </div>
</template>
